import { createContext, type PropsWithChildren, useContext, useMemo, useState } from 'react'
import { useLocalStorage } from '@dhl/use-storage'
import { type BasePageProps } from '../BasePage'

type PageState = {
  /**
   * When you need more then our default side menu width
   * @example 220
   * @example '12rem'
   *
   * @default '15rem' (240px)
   */
  sideMenuWidth?: NonNullable<BasePageProps['sideMenuOptions']>['width']

  /**
   * Is our SideMenu expanded or not
   */
  isExpanded: boolean

  /**
   * Internal prop to let our header know we also have a sidemenu
   * which is important information for showing hamburger menu
   */
  hasSideMenu: boolean

  /**
   * Wheter our side menu is open or not
   */
  isOpen: boolean

  /**
   * When you do not want the DHL logo to be printed
   *
   * @default false
   */
  noPrintHeader?: boolean
}

type PageActions = {
  /**
   * Expand/collapse your SideMenu
   */
  setIsExpanded: (arg: boolean) => void

  /**
   * Make sure our mobile hamburger menu is open/closed
   */
  setIsOpen: (arg: boolean) => void
}

type Context = PageState & PageActions

const defaultValue = {}

const defaultWidthExpanded = '15rem'

// really our max in small version, in expanded people can tweak themselves (also not advised)
const defaultWidthCollapsed = '6.5rem'

/**
 * Only exported for testability, not in our public api (index.ts)
 */
export const PageContext = createContext<Context>(defaultValue as Context)

type PageProviderProps = PropsWithChildren<
  {
    hasSideMenu?: boolean
  } & Pick<BasePageProps, 'sideMenuOptions' | 'noPrintHeader'>
>

export function PageProvider({
  children,
  sideMenuOptions = {
    isInitiallyOpen: true,
    width: defaultWidthExpanded,
    storageKey: 'page-expanded',
  },
  hasSideMenu = false,
  noPrintHeader = false,
}: PageProviderProps) {
  const [isExpanded, setIsExpanded] = useLocalStorage<PageState['isExpanded']>(
    sideMenuOptions?.storageKey || 'page-expanded',
    sideMenuOptions?.isInitiallyOpen ?? true
  )
  const [width, setWidth] = useState<PageState['sideMenuWidth']>(
    isExpanded ? sideMenuOptions?.width || defaultWidthExpanded : defaultWidthCollapsed
  )

  const [isOpen, setIsOpen] = useState<PageState['isOpen']>(false)

  const handleSetIsExpanded = (arg: boolean) => {
    setWidth(arg ? sideMenuOptions?.width || defaultWidthExpanded : defaultWidthCollapsed)
    setIsExpanded(arg)
  }

  const value = useMemo(
    (): Context => ({
      isExpanded,
      isOpen,
      hasSideMenu,
      sideMenuWidth: width,
      noPrintHeader,
      setIsExpanded: handleSetIsExpanded,
      setIsOpen,
    }),
    // reason: no circular refs when setting
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [width, isExpanded, isOpen, hasSideMenu, noPrintHeader]
  )

  return <PageContext.Provider value={value}>{children}</PageContext.Provider>
}

export function usePage() {
  const context = useContext(PageContext)

  if (context === defaultValue) {
    throw new Error('not used within a PageProvider.')
  }

  return context
}
